import {
  NodeInput,
  EdgeInput,
  NodeType,
} from '@rematter/frontend-lib-api-client';
import { Node, Edge } from '@xyflow/react';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';

function sanitizeConfig(
  config: Record<string, unknown>,
): Record<string, unknown> {
  const sanitized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(config)) {
    if (!key.startsWith('_')) {
      sanitized[key] = value;
    }
  }
  return sanitized;
}

export type AutomationValidateParams = {
  nodes: Node[];
  edges: Edge[];
};

/**
 * Converts a frontend AutomationNode to GraphQL NodeInput format for validation.
 */
function convertAutomationNodeToGraphQLInput(node: Node): NodeInput {
  const { id, type, position, data } = node;

  // Start with base fields
  const input: NodeInput = {
    id,
    type: type as NodeType,
    position: { x: position.x, y: position.y },
    label: data.label as string,
    description: data.description as string | undefined,
  };

  // Handle type-specific config transformations
  if (type === 'BusinessEventTrigger' && data.config) {
    const config = data.config as {
      source?: { case?: string; value?: { event?: { case?: string } } };
      filterExpression?: string;
    };

    // Transform source to eventType: "entityType.eventCase"
    if (config.source?.case) {
      const entityType = config.source.case;
      const eventCase = config.source.value?.event?.case;
      input.eventType = eventCase ? `${entityType}.${eventCase}` : entityType;
    }

    // Transform filterExpression to filter if needed
    if (config.filterExpression) {
      input.filter = { expression: config.filterExpression };
    }
  } else if (data.config && typeof data.config === 'object') {
    Object.assign(
      input,
      sanitizeConfig(data.config as Record<string, unknown>),
    );
  }

  return input;
}

/**
 * Converts a React Flow Edge to GraphQL EdgeInput format for validation.
 */
function convertReactFlowEdgeToGraphQLInput(edge: Edge): EdgeInput {
  return {
    id: edge.id,
    source: edge.source,
    target: edge.target,
    sourceHandle: edge.sourceHandle ?? null,
  };
}

export const automationValidateQueryAtom = createQueryAtomFactory(
  'automations.validate',
  (params: AutomationValidateParams) => ({
    automationValidate: {
      __args: {
        nodes: params.nodes.map(convertAutomationNodeToGraphQLInput),
        edges: params.edges.map(convertReactFlowEdgeToGraphQLInput),
      },
      valid: true,
      issues: {
        nodeId: true,
        field: true,
        code: true,
        message: true,
        severity: true,
      },
    },
  }),
);
